import type {
  AudienceSpendData,
  AudienceSpendSummaryData,
  GetLookalikeAudiencesStatsResponse
} from '@innovationdepartment/proxima-sdk-axios';
import { type DatePickerProps, useToaster } from '@innovationdepartment/proxima-ui';
import * as date from 'date-fns';
import { useAdAccountMasterToken, useAdManager, useIntegrationApi, useProximaSDK } from 'hooks';
import { useEffect, useState } from 'react';
import { useAdAccountStore, useBrandStore } from 'stores';
import type { BrandOverviewHookProps } from 'types/brandOverview';
import type { MasterTokenAdAccount } from 'types/integrations';

const on30DaysAgo = date.startOfDay(date.sub(new Date(), { days: 30 }));
const yesterday = date.startOfDay(date.endOfYesterday());
const dateFormatter = (d: Date | undefined) => date.format(d || new Date(), 'yyyy-MM-dd');

type BrandOverviewAdsDataHookProps = Pick<
  BrandOverviewHookProps,
  | 'dateRange'
  | 'audienceSpend'
  | 'audienceSpendSummary'
  | 'audienceStats'
  | 'last30DaysOfAllBrandSpend'
  | 'onAudienceDateRangeSelect'
  | 'onConnectAdAccount'
  | 'onDisconnectAdAccount'
  | 'loading'
  | 'apiLoading'
  | 'masterAdAccounts'
>;

function useBrandOverviewAdsData() {
  const [loading, setLoading] = useState<BrandOverviewHookProps['apiLoading']>({});
  const { getCampaigns, refreshAdAccount, disconnectIntegration } = useAdManager();
  const { connectMasterTokenAdAccount, retrieveMasterTokenAdAccounts } = useAdAccountMasterToken();
  const { fetchIntegrationsHistory } = useIntegrationApi();
  const { metaAdAccountsList, masterTokenAdAccountsList, updateMasterTokenAdAccountsList } =
    useAdAccountStore();

  /* audience usage state */
  const [dateRange, setDateRange] = useState<DatePickerProps['dateRange']>({
    endDate: yesterday,
    startDate: on30DaysAgo
  });
  const [audienceSpend, setAudienceSpend] = useState<AudienceSpendData>({
    averageDailySpend: 0,
    totalSpend: 0
  });
  const [last30DaysOfAllBrandSpend, setLast30DaysOfAllBrandSpend] = useState(0);
  const [audienceSpendSummary, setAudienceSpendSummary] = useState<AudienceSpendSummaryData>({
    monthlyAverageSpend: 0,
    lifetimeSpend: 0,
    last30DaysSpend: { total: 0, facebook: 0 }
  });
  const [audienceStats, setAudienceStats] = useState<GetLookalikeAudiencesStatsResponse>({
    activeAudiences: { total: 0, facebook: 0 },
    totalAudiences: { total: 0, facebook: 0 }
  });

  const audiencesApi = useProximaSDK('AudiencesApi');
  const audienceSpendApi = useProximaSDK('AudienceSpendApi');

  const { showToaster } = useToaster();
  const { brand } = useBrandStore();

  const handleLoading = (section: keyof BrandOverviewHookProps['apiLoading'], isLoading = true) => {
    setLoading(prevLoading => ({ ...prevLoading, [section]: isLoading }));
  };

  const onAudienceDateRangeSelect = (newDateRange: DatePickerProps['dateRange']) => {
    setDateRange(newDateRange);
  };

  const onRefreshMasterTokenAdAccounts = async () => {
    handleLoading('masterAdAccounts');
    await Promise.all([
      refreshAdAccount({ brandId: brand.brandId }),
      retrieveMasterTokenAdAccounts(),
      fetchIntegrationsHistory()
    ]);
    handleLoading('masterAdAccounts', false);
  };

  const onConnectAdAccount = async (adAccount: MasterTokenAdAccount) => {
    const connectedAdAccount = metaAdAccountsList.find(
      account => account.adAccountId === adAccount.accountId
    );

    if (connectedAdAccount) {
      showToaster({
        message: (
          <>
            Ad account&nbsp;<b>{adAccount.name}</b>&nbsp;already connected.
          </>
        ),
        variant: 'warning'
      });
      return;
    }

    try {
      handleLoading('masterAdAccounts');
      const response = await connectMasterTokenAdAccount(adAccount);
      if (!response) {
        handleLoading('masterAdAccounts', false);
        return;
      }
      await onRefreshMasterTokenAdAccounts();

      showToaster({
        message: (
          <>
            Ad account&nbsp;<b>{adAccount.name}</b>&nbsp;connected.
          </>
        ),
        variant: 'success'
      });

      updateMasterTokenAdAccountsList(
        masterTokenAdAccountsList.map(account =>
          account.accountId === adAccount.accountId ? { ...account, connected: true } : account
        )
      );
    } catch {
      // Error connecting ad account
      /*  */
    }

    handleLoading('masterAdAccounts', false);
  };

  const onDisconnectAdAccount = async (adAccount: MasterTokenAdAccount) => {
    // will disconnect the integration and remove ad account
    const connectedAdAccount = metaAdAccountsList.find(
      account => account.adAccountId === adAccount.accountId
    );
    // if the ad account is not connected, do nothing
    if (!connectedAdAccount) {
      showToaster({
        message: (
          <>
            Ad account&nbsp;<b>{adAccount.name}</b>&nbsp;not yet connected.
          </>
        ),
        variant: 'warning'
      });
      return;
    }
    handleLoading('masterAdAccounts');
    if (connectedAdAccount.integrationId) {
      const response = await disconnectIntegration(brand.brandId, connectedAdAccount.integrationId);
      if (response?.error) {
        handleLoading('masterAdAccounts', false);
        return;
      }
    }
    // wait for the ad accounts to refresh
    // to ensure the ad account is removed from the list via an integration deleted event
    // this is a temporary workaround to avoid the ad account being shown as connected after the integration is
    await new Promise(resolve => setTimeout(resolve, 1750));
    await onRefreshMasterTokenAdAccounts();
    showToaster({
      message: (
        <>
          Ad account&nbsp;<b>{adAccount.name}</b>&nbsp;disconnected.
        </>
      ),
      variant: 'warning'
    });
    updateMasterTokenAdAccountsList(
      masterTokenAdAccountsList.map(account =>
        account.accountId === adAccount.accountId ? { ...account, connected: false } : account
      )
    );
    handleLoading('masterAdAccounts', false);
  };

  /* fetch audience spend summary */
  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome migration; the app is already working as expected with these dependency arrays
  useEffect(() => {
    const getAudienceSpendSummary = async () => {
      if (!brand?.brandId) return;
      handleLoading('audiencesSpendSummary');

      try {
        const response = await audienceSpendApi.getBrandSpendSummary(
          { brandId: brand.brandId },
          { skipErrorToaster: true }
        );
        if (response.data) setAudienceSpendSummary(response.data);
      } catch {
        /* TODO(Jenky): handle error */
      }
      handleLoading('audiencesSpendSummary', false);
    };

    getAudienceSpendSummary();
  }, []);

  /* fetch audiences stats */
  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome migration; the app is already working as expected with these dependency arrays
  useEffect(() => {
    const fetchAudiencesStats = async () => {
      if (!brand?.brandId) return;
      handleLoading('audiencesStats');
      try {
        const response = await audiencesApi.getLookalikeAudiencesStats({ brandId: brand.brandId });
        if (response.data) setAudienceStats(response.data);
      } catch {
        /* TODO(Jenky): handle error */
      }
      handleLoading('audiencesStats', false);
    };

    fetchAudiencesStats();
  }, []);

  /* fetch data on date range changes */
  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome migration; the app is already working as expected with these dependency arrays
  useEffect(() => {
    const getAudienceSpendDateRange = async () => {
      if (!brand?.brandId) return;
      handleLoading('audiencesSpend');

      try {
        const formattedDates = {
          endDate: dateFormatter(dateRange.endDate),
          startDate: dateFormatter(dateRange.startDate)
        };
        const response = await audienceSpendApi.getBrandSpend({
          brandId: brand.brandId,
          ...formattedDates
        });
        if (response.data) setAudienceSpend(response.data);
      } catch {
        /* TODO(Jenky): handle error */
      }
      handleLoading('audiencesSpend', false);
    };

    getAudienceSpendDateRange();
  }, [dateRange]);

  /* fetch last 30 days of brand spend */
  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome migration; the app is already working as expected with these dependency arrays
  useEffect(() => {
    const getLast30DaysOfBrandSpend = async () => {
      if (!brand?.brandId) return;
      const firstMetaAdAccount = metaAdAccountsList.at(0);
      if (!firstMetaAdAccount) return;

      handleLoading('campaigns');

      try {
        const startDate = dateFormatter(on30DaysAgo);
        const endDate = dateFormatter(yesterday);
        const campaigns = await getCampaigns(
          { queryItems: { startDate, endDate } },
          firstMetaAdAccount.adAccountId
        );
        setLast30DaysOfAllBrandSpend(campaigns.data?.summary?.spend);
      } catch {
        /* catch error */
      }
      handleLoading('campaigns', false);
    };

    getLast30DaysOfBrandSpend();
  }, []);

  /* fetch master token ad accounts on load */
  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome migration; the app is already working as expected with these dependency arrays
  useEffect(() => {
    onRefreshMasterTokenAdAccounts();
  }, []);

  return {
    dateRange,
    audienceSpend,
    audienceSpendSummary,
    audienceStats,
    last30DaysOfAllBrandSpend,
    onAudienceDateRangeSelect,
    onConnectAdAccount,
    onDisconnectAdAccount,
    loading: Object.values(loading).some(Boolean),
    apiLoading: loading,
    masterAdAccounts: masterTokenAdAccountsList
  } satisfies BrandOverviewAdsDataHookProps;
}

export default useBrandOverviewAdsData;
